import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useNavigate, useParams } from "react-router-dom";
import { OdService } from "@/services/Od/OdService";
import { ThrowError } from "@/api/callApi";
import moment from "moment";
import { ArrowLeft, Calendar, FileText, Info, User } from "lucide-react";

const OdDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [od, setOd] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOd = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await OdService.getById(id);
      if (response?.status && response.data) {
        setOd(response.data);
      } else {
        setError("OD details not found");
      }
    } catch (err) {
      ThrowError(err);
      setError("Failed to load OD details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOd();
  }, [id]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner className="h-8 w-8 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !od) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-700">
            {error || "OD details not found"}
          </h3>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </Layout>
    );
  }

  const status = od.status?.toLowerCase() || "pending";
  let statusClasses = "text-amber-700 bg-amber-50 border-amber-200";
  if (status === "approved") {
    statusClasses = "text-green-700 bg-green-50 border-green-200";
  } else if (status === "rejected" || status === "cancelled") {
    statusClasses = "text-destructive bg-destructive/10 border-destructive/20";
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <MainHeading
            title="OD Detail"
            isBackButtonVisible
            onClick={() => navigate(-1)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Info className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  OD Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                <InfoItem
                  label="Date"
                  value={od.date ? moment(od.date).format("DD-MM-YYYY") : "—"}
                />
                <InfoItem label="Location" value={od.location} />
                <InfoItem label="Purpose / Reason" value={od.reason} />
                <InfoItem
                  label="Expected Return Time"
                  value={od.expectedReturnTime}
                />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Status
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase border capitalize ${statusClasses}`}
                  >
                    {status}
                  </span>
                </div>
              </div>
            </Card>

            {od.remarks && (
              <Card>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-base font-semibold">
                    Remarks
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {od.remarks}
                </p>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <User className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Applicant Details
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem label="Employee Name" value={od.user?.name || "—"} />
                <InfoItem label="Email Address" value={od.user?.email || "—"} />
                <InfoItem label="Phone" value={od.user?.phone || "—"} />
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Calendar className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Timeline
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Applied On"
                  value={
                    od.createdAt
                      ? moment(od.createdAt).format("DD-MM-YYYY HH:mm")
                      : "—"
                  }
                />
                {od.updatedAt && od.updatedAt !== od.createdAt && (
                  <InfoItem
                    label="Last Updated"
                    value={moment(od.updatedAt).format("DD-MM-YYYY HH:mm")}
                  />
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const InfoItem: React.FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className="text-sm font-semibold text-foreground capitalize">
      {value || "—"}
    </p>
  </div>
);

export default OdDetail;
